import React from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { Reservation } from "@types";
import { formatTimeSlotRange } from "utils/timeSlot";

interface ReservationUsedProps {
  legacy?: boolean;
  reservation?: Reservation;
}

const DELIVERY_TYPE_LABELS: Record<string, string> = {
  drop: "Dépose",
  install: "Installation",
  direct_drop: "Dépose directe",
  afterwork: "Afterwork",
};

const getDeliveryTypeLabel = (article: {
  drop_article: boolean;
  install_article: boolean;
  direct_drop_article: boolean;
}) => {
  if (article.install_article) return DELIVERY_TYPE_LABELS.install;
  if (article.direct_drop_article) return DELIVERY_TYPE_LABELS.direct_drop;
  return DELIVERY_TYPE_LABELS.drop;
};

const getDeliveryTypeClass = (article: {
  drop_article: boolean;
  install_article: boolean;
  direct_drop_article: boolean;
}) => {
  if (article.install_article) return "reservation-article__type--install";
  if (article.direct_drop_article)
    return "reservation-article__type--direct-drop";
  return "reservation-article__type--drop";
};

const ReservationUsed = ({
  legacy = false,
  reservation,
}: ReservationUsedProps) => {
  dayjs.locale("fr");

  if (!legacy || !reservation) {
    return (
      <div className="reservation-used">
        <div className="reservation-used__icon-wrapper">
          <div className="reservation-used__icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
        </div>
        <h2 className="reservation-used__title">Réservation utilisée</h2>
        <p className="reservation-used__message">
          Cette réservation a été utilisée pour planifier votre livraison.
        </p>
      </div>
    );
  }

  const formattedDate = reservation.date
    ? dayjs(reservation.date).format("dddd D MMMM YYYY")
    : "";

  const formattedTimeRange = (() => {
    if (!reservation.start || !reservation.stop) {
      if (reservation.halfday === "morning") return "Matin";
      if (reservation.halfday === "afternoon") return "Après-midi";
      return "";
    }

    return formatTimeSlotRange(reservation, "H[h]mm");
  })();

  const halfdayLabel =
    reservation.halfday === "morning"
      ? "Matin"
      : reservation.halfday === "afternoon"
      ? "Après-midi"
      : "";

  return (
    <div className="reservation-used-legacy">
      {/* Legacy header */}
      <div className="reservation-used-legacy__header">
        <div className="reservation-used-legacy__header-icon">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
          </svg>
        </div>
        <div>
          <h2 className="reservation-used-legacy__title">
            Réservation utilisée
          </h2>
          <p className="reservation-used-legacy__subtitle">
            Historique de la réservation — Utilisée le{" "}
            {reservation.expires_at
              ? dayjs(reservation.expires_at).format("D MMMM YYYY [à] H[h]mm")
              : formattedDate}
          </p>
        </div>
      </div>

      {/* Contact */}
      {reservation.phone_number && (
        <div className="reservation-used-legacy__card">
          <div className="reservation-used-legacy__card-header">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
            <span>Téléphone</span>
          </div>
          <div className="reservation-used-legacy__card-body">
            <a
              href={`tel:${reservation.phone_number}`}
              className="reservation-used-legacy__phone"
            >
              {reservation.phone_number}
            </a>
          </div>
        </div>
      )}
      {/* Date & Time */}
      <div className="reservation-used-legacy__card">
        <div className="reservation-used-legacy__card-header">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
          </svg>
          <span>Date et heure de livraison</span>
        </div>
        <div className="reservation-used-legacy__card-body">
          <div className="reservation-used-legacy__date">{formattedDate}</div>
          <div className="reservation-used-legacy__time-row">
            {halfdayLabel && (
              <span className="reservation-used-legacy__halfday">
                {halfdayLabel}
              </span>
            )}
            {formattedTimeRange && (
              <span className="reservation-used-legacy__time-range">
                {formattedTimeRange}
              </span>
            )}
          </div>
          <div className="reservation-used-legacy__badges">
            {reservation.delivery_type && (
              <span className="reservation-used-legacy__badge reservation-used-legacy__badge--type">
                {DELIVERY_TYPE_LABELS[reservation.delivery_type] ||
                  reservation.delivery_type}
              </span>
            )}
            {reservation.is_next_day && (
              <span className="reservation-used-legacy__badge reservation-used-legacy__badge--next-day">
                J+1
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Articles */}
      {reservation.articles && reservation.articles.length > 0 && (
        <div className="reservation-used-legacy__card">
          <div className="reservation-used-legacy__card-header">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12z" />
            </svg>
            <span>Articles ({reservation.articles.length})</span>
          </div>
          <div className="reservation-used-legacy__articles">
            {reservation.articles.map((article) => (
              <div key={article.id} className="reservation-article">
                <span className="reservation-article__quantity">
                  ×{article.quantity}
                </span>
                <div className="reservation-article__info">
                  <span className="reservation-article__name">
                    {article.article_name || "Article"}
                  </span>
                </div>
                <span
                  className={`reservation-article__type ${getDeliveryTypeClass(
                    article,
                  )}`}
                >
                  {getDeliveryTypeLabel(article)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationUsed;
